import type { Car, CarFilters, GasStation, GasStationFilters } from "@/types/database";

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-JM", {
    style: "currency",
    currency: "JMD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatFuelPrice(value: number | null | undefined): string {
  if (value == null || value === 0) return "—";
  return `$${value.toFixed(1)}`;
}

export function googleMapsUrlForStation(
  station: Pick<GasStation, "coordinatesLat" | "coordinatesLong" | "address" | "name" | "parish">
): string | null {
  if (station.coordinatesLat != null && station.coordinatesLong != null) {
    return `https://www.google.com/maps/search/?api=1&query=${station.coordinatesLat},${station.coordinatesLong}`;
  }

  const query = [station.name, station.address, station.parish].filter(Boolean).join(", ");
  if (!query.trim()) return null;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function carTitle(car: Car): string {
  const parts = [car.make, car.model, car.modelYear?.toString()].filter(Boolean);
  return parts.join(" ") || "Unknown car";
}

export function filterCars(cars: Car[], filters: CarFilters): Car[] {
  return cars.filter((car) => {
    const title = carTitle(car).toLowerCase();
    const search = filters.search.toLowerCase();
    if (search && !title.includes(search) && !(car.licensePlate?.toLowerCase().includes(search))) {
      return false;
    }
    if (filters.make && car.make !== filters.make) return false;
    if (filters.model && car.model !== filters.model) return false;
    if (filters.userID && car.userID?.toUpperCase() !== filters.userID.toUpperCase()) return false;
    if (filters.transmission && car.transmission !== filters.transmission) return false;
    if (filters.yearMin && (car.modelYear ?? 0) < Number(filters.yearMin)) return false;
    if (filters.yearMax && (car.modelYear ?? 9999) > Number(filters.yearMax)) return false;
    if (filters.body && car.body !== filters.body) return false;
    if (filters.color && car.color !== filters.color) return false;
    if (filters.valueMin && (car.value ?? 0) < Number(filters.valueMin)) return false;
    if (filters.valueMax && (car.value ?? Infinity) > Number(filters.valueMax)) return false;
    return true;
  });
}

export function filterGasStations(
  stations: GasStation[],
  filters: GasStationFilters
): GasStation[] {
  return stations.filter((station) => {
    const haystack = `${station.name} ${station.address} ${station.parish}`.toLowerCase();
    if (filters.search && !haystack.includes(filters.search.toLowerCase())) return false;
    if (filters.brand && station.name !== filters.brand) return false;
    if (filters.parish && station.parish !== filters.parish) return false;
    if (filters.hasCoords === "yes" && (station.coordinatesLat == null || station.coordinatesLong == null)) return false;
    if (filters.hasCoords === "no" && station.coordinatesLat != null && station.coordinatesLong != null) return false;
    const price = station.fuelPrice_87 ?? 0;
    if (filters.priceMin && price < Number(filters.priceMin)) return false;
    if (filters.priceMax && price > Number(filters.priceMax)) return false;
    return true;
  });
}

export function uniqueValues<T>(items: T[], getter: (item: T) => string | null | undefined): string[] {
  return [...new Set(items.map(getter).filter((v): v is string => Boolean(v)))].sort();
}

export function defaultCarFilters(): CarFilters {
  return {
    search: "",
    make: "",
    model: "",
    userID: "",
    yearMin: "",
    yearMax: "",
    transmission: "",
    body: "",
    color: "",
    valueMin: "",
    valueMax: "",
  };
}

export function userDisplayName(user: { firstName: string | null; lastName: string | null; id: number }): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || `User ${user.id}`;
}

export function defaultGasStationFilters(): GasStationFilters {
  return { search: "", brand: "", parish: "", hasCoords: "", priceMin: "", priceMax: "" };
}

export function isValidImageSrc(src: string | null | undefined): src is string {
  if (!src) return false;
  const trimmed = src.trim();
  return trimmed.length > 0 && (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("/"));
}

export function imageSrc(src: string | null | undefined): string | null {
  if (!isValidImageSrc(src)) return null;
  return src.trim();
}

export const CAR_IMAGE_FALLBACK =
  "https://qlxnmbgtohaiyhbzfvvc.supabase.co/storage/v1/object/public/Images/Cars/fallback%20car%20image.jpg";

export function carImageSrc(src: string | null | undefined): string {
  return imageSrc(src) ?? CAR_IMAGE_FALLBACK;
}

export function getErrorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (typeof record.error_description === "string") return record.error_description;
  }
  return fallback;
}
